import { Inject, Injectable } from '@angular/core';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import {
  determineIsMobile,
  filterNullUndefined,
  QueryParametersService,
} from '@domgen/dgx-fe-common';
import { GAService } from '@domgen/dgx-fe-ga';
import { ComponentStore } from '@ngrx/component-store';
import { RegistrationService } from '@us-registration/data-access';

import {
  CmsRegistrationOrderConfirmationPage,
  LocallyStoredRegistrationData,
  PlanDetails,
  REGISTRATION_CONFIG,
  RegistrationConfig,
  RegistrationCPGRequest,
  RegistrationCPGResponse,
  RegistriaUpdateUserOrderRequest,
  RegistriaUsersResponse,
} from '@us-registration/util-models';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

export interface ViewModel {
  cmsData: CmsRegistrationOrderConfirmationPage;
  isMobile: boolean;
  registeredAppliances?: LocallyStoredRegistrationData[];
  registrationCPG?: RegistrationCPGResponse;
  registriaUsersResponse?: RegistriaUsersResponse;
  showRegistrationCPG: boolean;
  planDetails?: PlanDetails;
  planDetailsAnnualPrice?: number;
}

export interface ComponentState {
  planDetailsAnnualPrice?: number;
  planDetails?: PlanDetails;
  planDetailsFromParams?: string;
  registeredAppliances?: LocallyStoredRegistrationData[];
  registrationCPG?: RegistrationCPGResponse;
  registrationCPGRequest?: RegistrationCPGRequest;
  registriaUsersResponse?: RegistriaUsersResponse;
  sendRegistriaOrderInfo?: boolean;
  showRegistrationCPG: boolean;
}

@Injectable()
export class RegistrationOrderConfirmationComponentService extends ComponentStore<ComponentState> {
  private readonly registeredAppliances$ = this.select(
    (state) => state.registeredAppliances
  );

  private readonly registriaUsersResponse$: Observable<
    RegistriaUsersResponse | undefined
  > = this.select((state) => state.registriaUsersResponse);

  private readonly registrationCPGRequest$: Observable<
    RegistrationCPGRequest | undefined
  > = this.select((state: ComponentState) => state.registrationCPGRequest);

  private readonly registrationCPG$: Observable<
    RegistrationCPGResponse | undefined
  > = this.select((state: ComponentState) => state.registrationCPG);

  private readonly sendRegistriaOrderInfo$: Observable<boolean | undefined> =
    this.select((state) => state.sendRegistriaOrderInfo);

  private readonly showRegistrationCPG$: Observable<boolean> = this.select(
    (state: ComponentState) => state.showRegistrationCPG
  );

  private readonly planDetails$: Observable<PlanDetails | undefined> =
    this.select((state: ComponentState) => state.planDetails);

  private readonly planDetailsAnnualPrice$: Observable<number | undefined> =
    this.select((state: ComponentState) => state.planDetailsAnnualPrice);

  private readonly planDetailsFromParams$: Observable<string | undefined> =
    this.select((state: ComponentState) => state.planDetailsFromParams);

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsData: this.cmsContainer.content$,
    isMobile: determineIsMobile(),
    registeredAppliances: this.registeredAppliances$,
    registrationCPG: this.registrationCPG$,
    registriaUsersResponse: this.registriaUsersResponse$,
    showRegistrationCPG: this.showRegistrationCPG$,
    planDetails: this.planDetails$,
    planDetailsAnnualPrice: this.planDetailsAnnualPrice$,
  });

  constructor(
    @Inject(REGISTRATION_CONFIG)
    private readonly registrationConfig: RegistrationConfig,
    private readonly cmsContainer: CmsContainer<CmsRegistrationOrderConfirmationPage>,
    private readonly registrationService: RegistrationService,
    private readonly queryParametersService: QueryParametersService,
    private readonly gaService: GAService
  ) {
    super({
      showRegistrationCPG: false,
    });
  }

  readonly updateRegistriaUserResponse = this.updater(
    (
      state: ComponentState,
      registriaUsersResponse?: RegistriaUsersResponse
    ) => ({
      ...state,
      registriaUsersResponse,
    })
  )(of(JSON.parse(localStorage.getItem('registriaUserResponse') as string)));

  readonly updateRegisteredAppliances = this.updater(
    (
      state: ComponentState,
      registeredAppliances?: LocallyStoredRegistrationData[]
    ) => ({
      ...state,
      registeredAppliances,
    })
  )(this.registeredAppliances);

  updateRegistrationCPG = this.updater(
    (state: ComponentState, registrationCPG?: RegistrationCPGResponse) => ({
      ...state,
      registrationCPG,
    })
  );

  readonly updateSendRegistriaOrderInfo = this.updater(
    (state: ComponentState, sendRegistriaOrderInfo?: boolean) => ({
      ...state,
      sendRegistriaOrderInfo,
    })
  )(this.registrationConfig.features.sendRegistriaOrderInfo);

  readonly updateShowRegistrationCPG = this.updater(
    (state: ComponentState, showRegistrationCPG: boolean) => ({
      ...state,
      showRegistrationCPG,
    })
  )(
    this.registrationCPG$.pipe(
      map((registrationCPG) => typeof registrationCPG !== 'string')
    )
  );

  readonly updatePlanDetails = this.updater(
    (state: ComponentState, planDetails: PlanDetails) => ({
      ...state,
      planDetails,
    })
  )(
    this.planDetailsFromParams$.pipe(
      filterNullUndefined(),
      map((planDetails) => JSON.parse(atob(planDetails)).results[0])
    )
  );

  readonly updatePlanDetailsAnnualPrice = this.updater(
    (state: ComponentState, planDetailsAnnualPrice: number | undefined) => ({
      ...state,
      planDetailsAnnualPrice,
    })
  )(
    this.planDetailsFromParams$.pipe(
      filterNullUndefined(),
      map((planDetails) => JSON.parse(atob(planDetails)).results[0].price)
    )
  );

  readonly updatePlanDetailsFromParams = this.updater(
    (state: ComponentState, planDetailsFromParams?: string) => ({
      ...state,
      planDetailsFromParams,
    })
  )(this.queryParametersService.get('result'));

  readonly updateRegistrationCPGRequest = this.updater(
    (
      state: ComponentState,
      registrationCPGRequest: RegistrationCPGRequest | undefined
    ) => ({
      ...state,
      registrationCPGRequest,
    })
  )(
    this.registeredAppliances$.pipe(
      filterNullUndefined(),
      map((registeredAppliances) => ({
        modelNumber: String(registeredAppliances[0].modelNumber),
      }))
    )
  );

  private readonly getRegistrationCPGOffer: Subscription = this.effect(
    (trigger$: Observable<RegistrationCPGRequest | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        switchMap((registrationCPGRequest) =>
          this.registrationService.getApplianceCPG(registrationCPGRequest)
        ),
        tap((registrationCPGResponse) =>
          this.updateRegistrationCPG(
            registrationCPGResponse as RegistrationCPGResponse
          )
        )
      )
  )(this.registrationCPGRequest$);

  private readonly updateRegistriaUserWithOrder: Subscription = this.effect(
    (
      trigger$: Observable<
        [
          PlanDetails | undefined,
          RegistriaUsersResponse | undefined,
          boolean | undefined
        ]
      >
    ) =>
      trigger$.pipe(
        filter(
          ([planDetails, registriaUsersResponse, sendRegistriaOrderInfo]) =>
            sendRegistriaOrderInfo === true
        ),
        map(
          ([planDetails, registriaUsersResponse, sendRegistriaOrderInfo]):
            | RegistriaUpdateUserOrderRequest
            | undefined => {
            if (!planDetails || !registriaUsersResponse) {
              return;
            }

            return {
              orderInfo: {
                sessionId: registriaUsersResponse.sessionId,
                orderNumber: planDetails.planNumber,
                amount: String(planDetails.price),
                orderDate: planDetails.effectiveDate,
              },
            };
          }
        ),
        filterNullUndefined(),
        switchMap((updateUserOrderRequest: RegistriaUpdateUserOrderRequest) =>
          this.registrationService.registriaUsers(updateUserOrderRequest)
        )
      )
  )(
    combineLatest([
      this.planDetails$,
      this.registriaUsersResponse$,
      this.sendRegistriaOrderInfo$,
    ])
  );

  private readonly trackOrderConfirmationGAPageView: Subscription = this.effect(
    (
      trigger$: Observable<
        [LocallyStoredRegistrationData[] | undefined, PlanDetails | undefined]
      >
    ) =>
      trigger$.pipe(
        tap(([registeredAppliances, planDetails]) => {
          if (registeredAppliances && planDetails) {
            const registeredAppliance: LocallyStoredRegistrationData =
              registeredAppliances[0];

            this.gaService.pageView({
              pagename: 'order-confirmation',
              applianceName: registeredAppliance.type,
              appliancePurchaseCost: registeredAppliance.purchasePrice,
              appliancePurchaseDate: registeredAppliance.purchaseDate,
              offerGeneratedPrice: planDetails.price,
              planNumber: planDetails.contractNumber,
              regSystemCode: localStorage.getItem('systemCode'),
              codeUsed: localStorage.getItem('registrationCodeUsed'),
            });
          }
        })
      )
  )(combineLatest([this.registeredAppliances$, this.planDetails$]));

  private readonly trackGAPurchaseEvent: Subscription = this.effect(
    (
      trigger$: Observable<
        [LocallyStoredRegistrationData[] | undefined, PlanDetails | undefined]
      >
    ) =>
      trigger$.pipe(
        tap(([registeredAppliances, planDetails]) => {
          const hasTrackedGAPurchaseEvent: boolean =
            localStorage.getItem('hasTrackedGAPurchaseEvent') === 'true';

          if (
            registeredAppliances &&
            planDetails &&
            !hasTrackedGAPurchaseEvent
          ) {
            const registeredAppliance: LocallyStoredRegistrationData =
              registeredAppliances[0];

            this.gaService.tag({
              event: 'purchase',
              ecommerce: {
                currency: 'US Dollar ($)',
                value: planDetails.price,
                transaction_id: localStorage.getItem('itbId'),
                regSystemCode: localStorage.getItem('systemCode'),
                items: [
                  {
                    appliance_purchase_cost: registeredAppliance.purchasePrice,
                    appliance_purchase_date: registeredAppliance.purchaseDate,
                    appliance_type: registeredAppliance.type,
                    appliance_warranty: true,
                    item_brand: registeredAppliance.brand,
                    item_category: registeredAppliance.type,
                    item_id: planDetails.contractNumber,
                    item_name: `${registeredAppliance.brand} ${registeredAppliance.type}`,
                    paymentType: 'D',
                    price: planDetails.price,
                    quantity: 1,
                  },
                ],
              },
            });
            localStorage.removeItem('itbId');
            localStorage.setItem('hasTrackedGAPurchaseEvent', 'true');
          }
        })
      )
  )(combineLatest([this.registeredAppliances$, this.planDetails$]));

  get registeredAppliances(): LocallyStoredRegistrationData[] | undefined {
    const confirmationData: string | null =
      localStorage.getItem('confirmationData');
    if (!confirmationData) return undefined;
    return JSON.parse(confirmationData);
  }
}
